import mammoth from 'mammoth';
import type { TemplateTypeConfig } from '../types/template';

const EMPTY_ARRAY: never[] = [];

export interface ValidationResult {
  isValid: boolean;
  foundTags: string[];
  missingTags: string[];
  duplicateTags: string[];
  error?: string;
}

/**
 * Extracts raw text from a DOCX file and validates the presence of required tags.
 * @param file The DOCX file to validate.
 * @param config The configuration containing required tags.
 * @returns Promise<ValidationResult>
 */
export const validateDocxTemplate = async (
  file: File,
  config: TemplateTypeConfig
): Promise<ValidationResult> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = result.value;

    // Find all {{ tag }} patterns
    const tagRegex = /{{(.*?)}}/g;
    const matches = text.match(tagRegex) || EMPTY_ARRAY;

    // Find all natural language INSERT instructions
    // Matches "INSERT" as a whole word until the end of the line or a pipe character.
    const insertRegex = /\bINSERT\b(?:(?!\s{2,}|\t)[^\n\r|])*/gi;
    const insertMatches = text.match(insertRegex) || EMPTY_ARRAY;

    // Clean up matches to remove any trailing whitespace
    const cleanInsertMatches = insertMatches.map((m) => m.trim());

    // Also look for literal strict tags from the config
    const literalTags = config.allowedTags
      .map((t) => t.tag)
      .filter((t) => !t.startsWith('{{') && t !== 'INSERT [Instruction]' && t !== 'EXAMPLE:')
      .filter((t) => text.includes(t));

    // Combine all matches and ensure exact string matches are deduplicated early
    const documentMatches = [...matches, ...cleanInsertMatches];
    const allMatchesRaw = [...documentMatches, ...literalTags];
    const allMatches = Array.from(new Set(allMatchesRaw));

    // Normalize tags for comparison (remove all whitespace)
    const normalize = (tag: string) => tag.replace(/\s+/g, '');

    const foundTagsNormalized = new Set(allMatches.map(normalize));

    // Specifically check for required tags, ignoring whitespace
    const missingTags = config.requiredTags.filter(
      (requiredTag) => !foundTagsNormalized.has(normalize(requiredTag))
    );

    // Identify duplicate tags based on actual document matches
    const tagCounts: Record<string, number> = {};
    for (const tag of documentMatches) {
      const normalized = normalize(tag);
      tagCounts[normalized] = (tagCounts[normalized] || 0) + 1;
    }

    // Find tags that appear more than once
    const duplicateTags = Object.entries(tagCounts)
      .filter(([_, count]) => count > 1)
      .map(
        ([normalizedKey]) =>
          allMatchesRaw.find((m) => normalize(m) === normalizedKey) || normalizedKey
      );

    return {
      isValid: missingTags.length === 0,
      foundTags: Array.from(new Set(allMatches)), // Return unique found tags for display/debugging
      missingTags, // Return missing tags in their original config format
      duplicateTags,
    };
  } catch (_error) {
    return {
      isValid: false,
      foundTags: [],
      missingTags: [],
      duplicateTags: [],
      error: 'Failed to read or parse the file.',
    };
  }
};
